import { Link } from "react-router-dom";
import { ArrowRight, LayoutGrid, Hexagon } from "@/lib/icons";
import AppHeader from "@/components/AppHeader";
import PeriodicTable from "@/components/PeriodicTable";
import DailyElementCard from "@/games/dailyElement/DailyElementCard";
import ChemDokuDailyCard from "@/games/chemDoku/ChemDokuDailyCard";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NAV_CATEGORIES } from "@/data/navigation";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

export default function Index() {
  return (
    <div className="relative min-h-screen bg-transparent">
      <div className="relative">
      <Seo title="ElementPad — Interactive Periodic Table & Chemistry Tools" description="Free interactive periodic table with 118 elements, 3D atoms, equation balancer, molar mass calculator, virtual lab and lesson plans for Grades 9-12." path="/" jsonLd={{"@context":"https://schema.org","@type":"WebSite","name":"ElementPad","url":"https://elementpad.net/"}} />
      <AppHeader />
      <Main className="py-6">
        {/* Hero - sets the tone, surfaces the IA in one glance */}
        <section
          className="max-w-[1200px] mx-auto px-4 mb-8 hero-glow"
          aria-labelledby="home-hero-title"
        >
          <h1
            id="home-hero-title"
            className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground"
          >
            Chemistry, made interactive.
          </h1>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl">
            Explore atoms, run a virtual lab, balance equations, and build worksheets  all in one place.
          </p>

          {/* Category quick-grid */}
          <div
            className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2"
            role="list"
            aria-label="Tool categories"
          >
            {NAV_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              // Categories with a dedicated hub route should link there.
              // Otherwise, send the user to the Explore page anchored at this
              // category so they see ALL sub-tools instead of being dropped
              // straight into the first one.
              const HUB_ROUTES: Record<string, string> = { play: "/play" };
              const targetPath =
                HUB_ROUTES[cat.key] ?? `/explore#cat-${cat.key}`;
              return (
                <Link
                  key={cat.key}
                  to={targetPath}
                  role="listitem"
                  data-tour={cat.key}
                  className="group flex flex-col gap-1 p-3 rounded-lg border border-border bg-card hover:border-primary/40 hover:bg-accent/30 transition-colors"
                  aria-label={`${cat.label} - ${cat.tagline}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon size={14} className="text-primary" aria-hidden="true" />
                    <span className="text-sm font-medium text-foreground">{cat.label}</span>
                  </div>
                  <span className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
                    {cat.tagline}
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="mt-3">
            <Link
              to="/explore"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <LayoutGrid size={12} aria-hidden="true" />
              Browse all tools
              <ArrowRight size={12} aria-hidden="true" />
            </Link>

            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="ml-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
                    aria-label="About the hexagon background motif"
                  >
                    <Hexagon size={12} aria-hidden="true" />
                    About this background
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="max-w-xs">
                  <p className="text-xs leading-snug">
                    The faint hexagons echo organic ring structures
                    (think benzene, C<sub>6</sub>H<sub>6</sub>) and the
                    six-fold symmetry common in molecular geometry: a
                    small visual cue from real chemistry.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </section>

        {/* Daily challenges - twin teaser cards */}
        <section
          className="max-w-[1200px] mx-auto px-4 mb-8"
          aria-labelledby="home-daily-title"
        >
          <h2
            id="home-daily-title"
            className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3"
          >
            Today's challenges
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DailyElementCard />
            <ChemDokuDailyCard />
          </div>
        </section>

        {/* Periodic table - still the centerpiece of the homepage */}
        <section
          className="max-w-[1200px] mx-auto px-4 mb-3"
          aria-labelledby="home-pt-title"
        >
          <h2 id="home-pt-title" className="text-2xl font-bold text-foreground mb-1">
            Periodic Table
          </h2>
          <p className="text-sm text-muted-foreground">
            Click any element to explore its properties, structure, and data.
          </p>
        </section>
        <div data-tour="periodic-table">
          <PeriodicTable />
        </div>
      </Main>
      </div>
    </div>
  );
}
